import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config/apiClientConfig';

const answerSchema = z.object({
	id: z.number(),
	questionId: z.number(),
	answerText: z.string(),
	answerParentId: z.number().nullish(),
	isCustomAnswer: z.boolean(),
});

const questionSchema = z.object({
	id: z.number(),
	questionText: z.string(),
	dataTypeId: z.number(),
	parentQuestionId: z.number().nullable(),
	answers: z.array(answerSchema),
	savedAnswerId: z.number().nullish(),
	savedAnswer: z.string().nullish(),
});

const memberQuestionsSchema = z.object({
	memberId: z.string(),
	isMedicalConditionDeclared: z.boolean(),
	height: z.number(),
	weight: z.number(),
	questions: z.array(questionSchema),
});

const quoteDetailSchema = z.object({
	quoteListNo: z.string(),
	quotePlanReferenceNumber: z.string(),
	customerEmail: z.string().nullish(),
	insuranceCompanyId: z.number().optional(),
	planId: z.number().optional(),
	policyTypeId: z.number().optional(),
	premiumExcVAT: z.number().optional(),
	vatOnPremium: z.number().optional(),
	totalAnnualPremium: z.number().optional(),
	quoteExpiryDate: z.string().optional(),
	emirateId: z.number().optional(),
	memberCount: z.number().optional(),
});

const memberInfoSchema = z.object({
	emiratesIdNumber: z.string().nullish(),
	nationalityId: z.number().optional(),
	occupation: z.string().optional(),
	dateOfBirth: z.string().optional(),
	age: z.number().optional(),
	gender: z.number().optional(),
	passportNo: z.string().optional(),
	salaryBandId: z.number().nullish(),
	principalSalaryBand: z.number().optional(),
	maritalStatus: z.number().optional(),
	weight: z.number().optional(),
	height: z.number().optional(),
	isMedicalConditionDeclared: z.boolean(),
	memberId: z.string().optional(),
	unifiedNumber: z.string().nullish(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	relationshipType: z.number().optional(),
	visaFileNumber: z.string().optional(),
	mafDocumentPath: z.string().optional(),
	priceWithoutVat: z.number().optional(),
	vatAmount: z.number().optional(),
	priceWithVat: z.number().optional(),
	isPrincipal: z.boolean().optional(),
	bmiLoading: z.number().optional(),
	vatOnBMILoading: z.number().optional(),
});

const getQuoteDetailsResponseSchema = z.object({
	quoteDetail: quoteDetailSchema,
	members: z.array(memberInfoSchema),
	memberQuestions: z.array(memberQuestionsSchema),
});

type MemberQuestion = {
	memberId: string;
	isMedicalConditionDeclared: boolean;
	height: number;
	weight: number;
	questions: {
		id: number;
		questionText: string;
		dataTypeId: number;
		parentQuestionId: number | null;
	}[];
	answers: {
		id: number;
		questionId: number;
		answerText: string;
		isCustomAnswer: boolean;
		answerParentId: number | null;
	}[];
	savedMedicalAnswers: {
		id: number;
		memberId: string;
		questionId: number;
		answerId?: number;
		answer?: string | null;
	}[];
};

const transformMemberData = (memberQuestion: MemberQuestion) => {
	const { questions, answers, savedMedicalAnswers, ...rest } = memberQuestion;

	const finalQuestions = questions.map((q) => {
		return {
			...q,
			answers: answers.filter((v) => v.questionId === q.id).sort((a, b) => a.id - b.id) ?? [],
			savedAnswerId: savedMedicalAnswers?.find((v) => v.questionId === q.id)?.answerId ?? null,
			savedAnswer: savedMedicalAnswers.find((v) => v.questionId === q.id)?.answer ?? null,
		};
	});

	return { ...rest, questions: finalQuestions };
};

const getQuoteDetails = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/admin/quote/:quotePlanReferenceNumber/details',
		method: 'GET',
		responseSchema: getQuoteDetailsResponseSchema,
		requestSchema: z.null().optional(),
		requestParameters: { quotePlanReferenceNumber: ['required', 'string'] },
		transformResponse: (response: {
			data: {
				quoteDetail: z.infer<typeof quoteDetailSchema>;
				members: z.infer<typeof memberInfoSchema>[];
				memberQuestions: MemberQuestion[];
			};
		}) => ({
			...response.data,
			memberQuestions: response.data.memberQuestions.map((m) => transformMemberData(m)),
		}),
	});

export default getQuoteDetails;

export type GetQuoteDetailsAPIResponse = TypeOfAPIResponse<ReturnType<typeof getQuoteDetails>>;

export type GetQuoteDetailsError = TypeOfAPIError<ReturnType<typeof getQuoteDetails>>;
